import {
  ApiError,
  ApiErrorInitialState,
  ApiFunctionsStatus,
  Industry,
} from '@hellodarwin/core/lib/features/entities';
import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { RootState } from '../../../app/app-store';
import showErrorNotification from '../../utils/show-error-notifications';
import ClientApi from '../client-api';
import ClientApiV2 from '../client-api-v2';

export interface TagsState {
  status: ApiFunctionsStatus;
  error: ApiError;
  industries: Industry[];
  bottomLevelIndustries: Industry[];
}

const initialState: TagsState = {
  status: { general: 'idle' },
  error: ApiErrorInitialState,
  industries: [],
  bottomLevelIndustries: [],
};

export const fetchIndustries = createAsyncThunk<
  Industry[],
  { api: ClientApi; locale: string },
  { rejectValue: ApiError }
>(
  'client/fetchIndustries',
  async (
    { api, locale }: { api: ClientApi; locale: string },
    { rejectWithValue },
  ) => {
    try {
      return await api.fetchIndustries(locale);
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchBottomLevelIndustries = createAsyncThunk<
  Industry[],
  { api: ClientApiV2; locale: string },
  { rejectValue: ApiError }
>(
  'client/fetchBottomLevelIndustries',
  async (
    { api, locale }: { api: ClientApiV2; locale: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await api
        .createAxios()
        .get<Industry[]>(`/tags/industries/naics?locale=${locale}`);
      return response.data;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

const tagsSlice = createSlice({
  name: 'tags',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchIndustries.pending, (state) => {
      state.status['fetchIndustries'] = 'pending';
    });
    builder.addCase(fetchIndustries.fulfilled, (state, { payload }) => {
      state.industries = payload;
      state.status['fetchIndustries'] = 'idle';
    });
    builder.addCase(fetchIndustries.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status['fetchIndustries'] = 'idle';
    });
    builder.addCase(fetchBottomLevelIndustries.pending, (state) => {
      state.status['fetchBottomLevelIndustries'] = 'pending';
    });
    builder.addCase(
      fetchBottomLevelIndustries.fulfilled,
      (state, { payload }) => {
        state.bottomLevelIndustries = payload;
        state.status['fetchBottomLevelIndustries'] = 'idle';
      },
    );
    builder.addCase(
      fetchBottomLevelIndustries.rejected,
      (state, { payload }) => {
        state.error = payload ?? ApiErrorInitialState;
        state.status['fetchBottomLevelIndustries'] = 'idle';
      },
    );
  },
});

export const selectIndustriesSectors = createSelector(
  (state: RootState) => state.tags.industries,
  (industries): { label: string; value: string }[] => {
    return industries
      .filter((item) => item.level === 1)
      .map((item) => {
        return {
          label: `${item.id} - ${item.class_title}`,
          value: item.id,
        };
      });
  },
);

export const selectNaicsCodes = createSelector(
  [(state: RootState) => state.tags.bottomLevelIndustries],
  (bottomLevelIndustries) =>
    Array.isArray(bottomLevelIndustries) && bottomLevelIndustries.length > 0
      ? bottomLevelIndustries
          .map((item) => {
            return {
              label: `${item.id} - ${item.class_title}`,
              value: item.id,
            };
          })
          .sort((a, b) => a.value.localeCompare(b.value))
      : [],
);

export const selectIndustriesSubsectors = createSelector(
  (state: RootState) => state.tags.industries,
  (industries) => industries.filter((item: Industry) => item.level === 2),
);

export const selectIndustriesSubsectorsForFundingExplorer = createSelector(
  [
    (state: RootState, sectors: string[]) => ({
      industries: state.tags.industries,
      sectors,
    }),
  ],
  ({ industries, sectors }) =>
    industries
      .filter(
        (item: Industry) =>
          item.level === 2 && sectors.some((sector) => sector === item.parent),
      )
      .map((item) => {
        return {
          label: `${item.id} - ${item.class_title}`,
          value: item.id,
        };
      })
      .sort((a, b) => a.value.localeCompare(b.value)),
);

export const selectCitiesForFundingExplorer = createSelector(
  [
    (state: RootState, regions: string[]) => ({
      cities: state.grants.cities,
      regions,
    }),
  ],
  ({ cities, regions }) => {
    if (!cities) return [];
    // If no regions selected, return all cities
    if (!regions || regions.length === 0) {
      return cities.map((item) => ({
        label: item.city,
        value: item.city,
        mrc: item.mrc,
        province: item.province,
      }));
    }
    // Filter cities to only include those whose province is in selected regions
    return cities
      .filter((city) => regions.includes(city.province))
      .map((item) => ({
        label: item.city,
        value: item.city,
        mrc: item.mrc,
        province: item.province,
      }));
  },
);

export const selectTagsIsLoading = createSelector(
  [
    (state: RootState, _?: string) => state.tags.status,
    (_: RootState, functionName?: string) => functionName,
  ],
  (status, functionName) => {
    if (functionName) {
      return status[functionName] === 'pending';
    }
    return !!Object.keys(status).find((s) => s === 'pending');
  },
);

export const selectIndustrySectorsMap = createSelector(
  [(state: RootState) => state.tags.industries],
  (industries) => {
    if (!Array.isArray(industries) || industries.length === 0)
      return {} as Record<string, Industry>;
    return industries
      .filter((item) => item.level === 1)
      .reduce(
        (acc: Record<string, Industry>, item: Industry) => {
          acc[item.id] = item;
          return acc;
        },
        {} as Record<string, Industry>,
      );
  },
);

export const tagsReducer = tagsSlice.reducer;
